import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { housingTypes, type BoreholeHousingType, type BoreholeHousingTypeInput } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { Pagination, usePaginated } from '@/components/Pagination';

/**
 * Admin: katalog typów obudów studni. Definiuje kod (BETONOWA), nazwę, opis i kolejność.
 * Użytkownicy w edytorze studni wybierają z tej listy w sekcji "Obudowa studni".
 */
export function HousingTypeAdmin() {
  const [rows, setRows] = useState<BoreholeHousingType[] | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<BoreholeHousingType | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => {
    setErr(null);
    setRows(null);
    housingTypes.list(includeInactive).then(setRows).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [includeInactive]);

  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(rows ?? [], { storageKey: 'housing-types' });

  const onDeactivate = async (t: BoreholeHousingType) => {
    if (!confirm(`Dezaktywować typ „${t.code} — ${t.name}"? Można ponownie aktywować edytując pole "active".`)) return;
    try {
      await housingTypes.delete(t.id);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Typy obudów studni</h1>
          <div className="text-ink-400 text-[13px]">
            Katalog typów obudów (np. Betonowa, Stalowa, Prefabrykowana). Użytkownicy wybierają z tej listy
            w sekcji „Obudowa studni" w edytorze otworu.
          </div>
        </div>
        <div className="flex gap-2">
          <label className="text-[12px] text-ink-700 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)}/>
            Pokaż dezaktywowane
          </label>
          <button onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Nowy typ
          </button>
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="card">
        <div className="card-body !p-0">
          {!rows && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {rows && rows.length === 0 && <div className="p-4 text-ink-400">Brak typów. Dodaj pierwszy.</div>}
          {rows && rows.length > 0 && (
            <>
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>Nazwa</th>
                  <th>Opis</th>
                  <th className="w-20">Sort</th>
                  <th className="w-24">Status</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(t => (
                  <tr key={t.id} className={t.active ? '' : 'opacity-50'}>
                    <td className="font-mono text-ink-800">{t.code}</td>
                    <td className="text-ink-900">{t.name}</td>
                    <td className="text-[12px] text-ink-500 max-w-[420px] truncate" title={t.description ?? ''}>
                      {t.description ?? '—'}
                    </td>
                    <td className="font-mono text-[11px] text-ink-500">{t.sortOrder}</td>
                    <td>{t.active
                      ? <span className="pill pill-green">aktywny</span>
                      : <span className="pill pill-amber">dezakt.</span>}</td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => setEditing(t)} className="text-water-600 hover:text-water-800 p-1" title="Edytuj">
                        <Icon name="edit" className="w-3.5 h-3.5"/>
                      </button>
                      {t.active && (
                        <button onClick={() => onDeactivate(t)} className="text-red-500 hover:text-red-700 p-1 ml-1" title="Dezaktywuj">
                          <Icon name="x" className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 border-t border-ink-100">
              <Pagination total={total} page={page} pageSize={pageSize}
                          onPageChange={setPage} onPageSizeChange={setPageSize}/>
            </div>
            </>
          )}
        </div>
      </div>

      {(creating || editing) && (
        <HousingTypeEditModal
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

function HousingTypeEditModal({ initial, onClose, onSaved }: {
  initial: BoreholeHousingType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = initial != null;
  const [form, setForm] = useState<BoreholeHousingTypeInput>({
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    sortOrder: initial?.sortOrder ?? 100,
    active: initial?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!form.code.trim()) { setErr('Kod jest wymagany'); return; }
    if (!form.name.trim()) { setErr('Nazwa jest wymagana'); return; }
    setBusy(true); setErr(null);
    try {
      if (isEdit && initial) await housingTypes.update(initial.id, form);
      else await housingTypes.create(form);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: form, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[600px] w-full" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <h2 className="text-[16px] font-semibold m-0">{isEdit ? 'Edycja typu obudowy' : 'Nowy typ obudowy'}</h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>

        <div className="p-5 space-y-3">
          {err && <div className="text-red-500 text-[12px]">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Kod (np. BETONOWA)*">
              <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                     className="input font-mono"/>
            </Field>
            <Field label="Nazwa*">
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     placeholder="np. Betonowa" className="input"/>
            </Field>
          </div>
          <Field label="Opis (opcjonalny)">
            <textarea value={form.description ?? ''}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      placeholder="Krótki opis typu, np. zakres średnic, materiał, charakterystyka"
                      className="input min-h-[60px]" rows={3}/>
          </Field>
          <Field label="Kolejność sortowania">
            <input type="number" value={form.sortOrder ?? 100}
                   onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })}
                   className="input font-mono w-32"/>
          </Field>

          {isEdit && (
            <label className="flex items-center gap-2 text-[12px] text-ink-700 cursor-pointer">
              <input type="checkbox" checked={form.active ?? true}
                     onChange={e => setForm({ ...form, active: e.target.checked })}/>
              Aktywny (widoczny w dropdownie edytora studni)
            </label>
          )}
        </div>

        <div className="border-t border-ink-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={requestClose} disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy} className="btn btn-primary btn-sm">
            {busy ? 'Zapisywanie…' : (isEdit ? 'Zapisz zmiany' : 'Dodaj typ')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      {children}
    </div>
  );
}
